import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === (i18n.language || "").slice(0, 2)) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const change = (e, code) => {
    e.preventDefault();
    e.stopPropagation();
    i18n.changeLanguage(code);
    try { localStorage.setItem("chronos.lang", code); } catch (err) { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="lang-switcher" ref={rootRef}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="glass rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs hover:border-amber-400/50 transition-colors relative z-[75]"
        aria-label="Language"
        aria-expanded={open}
        data-testid="lang-switcher-btn"
      >
        <Globe className="w-3.5 h-3.5 text-amber-300" />
        <span>{current.flag}</span>
        {!compact && <span className="hidden sm:inline text-stone-200 font-bold">{current.code.toUpperCase()}</span>}
      </button>
      {open && (
        <>
          {/* Invisible backdrop catches ALL clicks (works even for disabled buttons underneath) */}
          <div
            className="fixed inset-0 z-[65]"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            onTouchStart={(e) => { e.stopPropagation(); setOpen(false); }}
            data-testid="lang-backdrop"
          />
          <div
            className="absolute right-0 mt-2 z-[70] glass-strong rounded-2xl p-2 min-w-[180px] max-h-[75vh] overflow-y-auto shadow-2xl"
            data-testid="lang-menu"
            onClick={(e) => e.stopPropagation()}
          >
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={(e) => change(e, lang.code)}
                data-testid={`lang-option-${lang.code}`}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-white/5 ${
                  lang.code === current.code ? "text-amber-300" : "text-stone-200"
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1 font-sans" style={{ direction: lang.dir === "rtl" ? "rtl" : "ltr" }}>{lang.label}</span>
                {lang.code === current.code && <Check className="w-3.5 h-3.5 text-amber-300" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
